/**
 * The shared-list backend seam — and, since RULINGS **R48**, a real one.
 *
 * Authorised by product-concept §10.1 ("shared lists... web link fallback so a partner
 * without the app can still add 'bin bags' from a browser") and its objection 3, which
 * explicitly rejected "web fallback is scope creep" — "for shared shopping lists it is
 * the difference between the feature working in a real household and not";
 * implementation-brief §2 and §7 (commutative list ops, push-plus-poll sync).
 * `makeItem`/`mergeItems`/`checkItem`/`uncheckItem`/`isChecked` all come from
 * `@carn/core` unmodified — this file never reimplements list logic (`@carn/core`'s
 * `lists.ts` header: "There is only one merge implementation in this codebase").
 *
 * ## What changed, and what the old header used to have to admit
 *
 * This file used to document a gap: every `/api/v1/lists/*` route required a verified
 * Clerk bearer token, there was no "GET the items on list X" route at all, and so the
 * exact scenario §10.1 names had no endpoint. R48 closed that. The endpoint is
 * `functions/handlers/shared-list-link.ts` at `/api/v1/shared-lists/:token[/items[/:id]]`,
 * and `RemoteApiBackend` below is now a client for it rather than a client for a shape
 * nothing unauthenticated could reach.
 *
 * ## Which backend is running, and why the page says so
 *
 * R48: "`apps/web`'s `RemoteApiBackend` becomes the active backend when a token is
 * present in the URL, with `LocalStorageBackend` as the offline fallback — and the page
 * states which one it is running, because an honest preview beats a false claim of sync."
 * `ShareLinkBackend` is that composition, and `SharedListStatus` is the thing the page
 * renders:
 *
 *   `preview`  — NO TOKEN IN THE URL AT ALL. Nothing leaves the browser, because there is
 *                nowhere to send it. `share.localOnly` says so, and on this branch that
 *                sentence is true.
 *   `loading`  — a token is present and the first read is in flight (`sync.syncing`).
 *   `live`     — the server is answering; this is real sync (`sync.synced`).
 *   `revoked`  — A TOKEN IS PRESENT AND THE SERVER SAYS IT OPENS NOTHING (404: unknown,
 *                revoked, or the list is gone — one indistinguishable answer, on purpose).
 *                RULINGS **R68**: `sharedList.linkExpired`, "This link no longer works. Ask
 *                for a new one." This used to collapse into `preview`, which rendered
 *                `share.localOnly` — "This phone only for now" — on a page that can be a
 *                desktop browser, while the visitor's typed item went to `localStorage` and
 *                was never queued and never could be. Nothing on screen said the link was
 *                dead. Two failures in one screen, and this state is both fixes: the page
 *                states the fact, and `addItem`/`setChecked` below refuse rather than
 *                pretending.
 *   `rateLimited` — the server answered 429. It was reached; it refused. Its own sentence
 *                (`sharedList.rateLimited`), because this used to render `sync.failed`
 *                ("Could not reach the server... it will try again"), which is false in both
 *                halves. Writes ARE queued and ARE replayed, so the state is recoverable —
 *                which is why it is not the same state as `revoked`.
 *   `degraded` — a token is present but the server is unreachable. Reads come from the
 *                last snapshot, writes go to a small local outbox, and the outbox is
 *                replayed on the next successful read — which is what makes
 *                `sync.failed`'s "it will try again" a true sentence rather than a
 *                comforting one. Implementation-brief §2's "the local write is the real
 *                write" applied honestly to a browser tab.
 *
 * ## The token is in a header, not a path (RULINGS R67)
 *
 * `RemoteApiBackend` used to interpolate the credential into the request path, which is
 * recorded by Netlify's access log. R67 rules it into the `x-carn-share-token` header the
 * router already accepted, against the placeholder path `/api/v1/shared-lists/-/…`, and the
 * link the owner shares into the URL **fragment** (`App.tsx`). The path form stays supported
 * server-side and is documented as the weaker one.
 *
 * ## The wire types are DECLARED here, not imported
 *
 * `SharedListItemDto` below mirrors `functions/src/lib/share-link.ts`, which is the
 * authority on the shape. `apps/web` cannot import from `functions/` (they are separate
 * workspaces with separate dependency sets, and a shared contract package would be a new
 * dependency this task is not authorised to add — CLAUDE.md). Change one, change the
 * other; the field list is short on purpose.
 *
 * NOTE what the wire does NOT carry: no `department` (R58 — "the web fallback stays a
 * flat add-and-check list ... Do not add department grouping"; the browser is not sent a
 * department, so this surface *cannot* group by one), no `addedBy`, no `check.by`
 * (account data, R48), no `capturedPlaceLabel` (implementation-brief §3).
 */
import {
  checkItem,
  isChecked,
  makeItem,
  mergeItems,
  uncheckItem,
  UNSORTED_SECTION,
  type ListItem,
} from '@carn/core';

// ---------------------------------------------------------------------------
// The seam
// ---------------------------------------------------------------------------

export type LoadedList = {
  /** The real list id once the server has told us; `PENDING_LIST_ID` until then. */
  listId: string;
  /** The list's own name, when the server sent one. Never invented. */
  name?: string;
  items: ListItem[];
};

export type SharedListStatus =
  | 'preview'
  | 'loading'
  | 'live'
  | 'revoked'
  | 'rateLimited'
  | 'degraded';

/**
 * Something the page must SAY, as opposed to a state it must be in.
 *
 * Exactly one kind, and it exists because of one silent failure: a replayed add against an
 * item the household has since removed used to come back 200 with the removed item's own
 * projection, so the browser dropped the outbox entry, merged the item into its local view,
 * and let the next `load()` quietly overwrite it — the item appeared and vanished with
 * nothing said, on the one surface where the visitor has no app to check. The route now
 * answers `410 item_removed` and this carries the sentence
 * (`sharedList.itemWithdrawn`).
 *
 * Not a status, because it is not a state the page is IN: the link is fine, sync is fine, one
 * item did not land. Keeping the two apart is what stops "one item was withdrawn" from
 * reading as "the link is broken".
 */
export type SharedListNotice = { kind: 'itemWithdrawn'; itemName: string };

/**
 * One backend instance is bound to ONE list, which is why nothing here takes a `listId`
 * parameter any more: with a capability URL the client does not know the list id until
 * the server answers (the token is scoped to a list, not the other way round — R48).
 */
export interface ShoppingListBackend {
  /** Load the current items. Also how the client learns which list it is looking at. */
  load(): Promise<LoadedList>;
  /** Add a new item. Concurrent adds both survive by construction (brief §2). */
  addItem(item: ListItem): Promise<void>;
  /** Check or uncheck. Unchecking is a correction and must stick (core `lists.ts`). */
  setChecked(itemId: string, checked: boolean, by: string): Promise<void>;
  /**
   * Called with the fresh list whenever it changes for a reason OTHER than the caller's
   * own action — another tab (`LocalStorageBackend`) or the poll (brief §7's
   * push-plus-poll, minus the push, which has nowhere to be delivered in a browser tab
   * belonging to somebody with no account). Returns an unsubscribe function.
   */
  subscribe(onChange: (list: LoadedList) => void): () => void;
}

/** Used as a storage key before the server has told us the real list id. */
export const PENDING_LIST_ID = 'pending';

/**
 * How often the tab re-reads the list while it is visible.
 *
 * SET TOGETHER WITH `SHARE_LINK_LIMITS` in `functions/src/lib/rate-limit.ts` — 30s is 120
 * requests/hour per tab, against a 1200/hour per-token cap. That cap was 300/hour and was
 * justified against ONE tab, which is not the shape of a link forwarded to a group chat: two
 * members with the page open was 240/hour before anybody added anything, and three was over.
 * The budget is now eight tabs polling (960/hour) plus 240/hour of adds and checks. Shorten
 * this and you must raise that. Polling stops entirely when the tab is hidden, which is both
 * the courteous thing and part of why the arithmetic works.
 */
export const SHARE_LINK_POLL_MS = 30_000;

// ---------------------------------------------------------------------------
// The wire (see the header: mirrors functions/src/lib/share-link.ts)
// ---------------------------------------------------------------------------

type SharedListItemDto = {
  id: string;
  name: string;
  qualifier?: string;
  addedAt: number;
  updatedAt: number;
  check?: { at: number };
  uncheck?: { at: number };
};

type SharedListDto = {
  list: { id: string; name: string };
  items: SharedListItemDto[];
};

/**
 * Thrown on any non-2xx. `status` is what tells a dead link apart from a dead network, a
 * refusal apart from an outage, and a withdrawn item apart from all three.
 *
 * `code` is the route's own `error.code` when it sent one (`not_found`, `rate_limited`,
 * `item_removed`, `bad_request`, `conflict`). The message deliberately carries the status
 * only: the response body is machine-facing English from the API and is never rendered, and
 * nothing about a failed request may end up in a string that could be logged with a token
 * near it.
 */
export class ShareLinkError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`share link request failed: ${status}`);
    this.name = 'ShareLinkError';
  }
}

/**
 * Which state a failed request leaves the page in. One place, so the four outcomes cannot
 * drift apart between `load`, `addItem` and `setChecked`.
 *
 * 404 is `revoked` and not `degraded`: the server answered. 429 is `rateLimited` and not
 * `degraded`: the server answered and refused. Anything else — a thrown `fetch`, a 500, a
 * gateway page — is genuinely "could not reach the server".
 */
function statusForError(err: unknown): SharedListStatus {
  if (!(err instanceof ShareLinkError)) return 'degraded';
  if (err.status === 404) return 'revoked';
  if (err.status === 429) return 'rateLimited';
  return 'degraded';
}

/**
 * Pull `error.code` off a failure body, tolerantly. A route that answered with a shape we do
 * not recognise, or with no body at all (a platform 502 page), is not an error worth having
 * an error about — the status alone is enough to decide what the page says.
 */
async function readErrorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { error?: { code?: unknown } };
    const code = body?.error?.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

function fromDto(dto: SharedListItemDto, listId: string): ListItem {
  return {
    id: dto.id,
    listId,
    name: dto.name,
    qualifier: dto.qualifier,
    /**
     * R58: the wire carries no section, so every item here is the catch-all. This is not a
     * claim about the item's real section — the app holds that, and this surface has no
     * business rendering or editing it.
     *
     * R125 made withholding it STRONGER rather than incidental. A section used to be one of
     * twelve supermarket departments, so leaking one would have disclosed roughly nothing;
     * a section can now be a name the household typed, so the field is potentially "Mum" or
     * "Rhys". A link anyone can open is exactly the wrong place for that, and
     * `SharedListItemDto` in `functions/src/lib/share-link.ts` withholds it at the source.
     */
    section: UNSORTED_SECTION,
    check: dto.check,
    uncheck: dto.uncheck,
    addedAt: dto.addedAt,
    updatedAt: dto.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// The R48 client
// ---------------------------------------------------------------------------

export type RemoteApiBackendOptions = {
  /** The capability token from the URL. This IS the credential. */
  token: string;
  /** e.g. the deployed Functions origin, or '' for same-origin. */
  apiBase?: string;
};

/**
 * The path segment that stands where the token used to be.
 *
 * `readToken` in `functions/src/lib/share-link.ts` prefers the `x-carn-share-token` header
 * and ignores the path segment entirely when it is present, so this is a placeholder rather
 * than a value — it exists because the route shape needs a first segment. `-` is outside
 * `TOKEN_SHAPE`'s charset, so a request that somehow lost its header fails closed with the
 * ordinary indistinguishable 404 rather than being read as a token.
 */
export const TOKEN_IN_HEADER_PATH = '-';

/**
 * A client for `/api/v1/shared-lists/...` — the ONE unauthenticated route family in
 * `functions/`, built by R48. No Clerk token, no account, no sign-in.
 *
 * THE TOKEN GOES IN A HEADER (RULINGS R67). It used to be interpolated into the request
 * path, which Netlify's access log records; R67 rules that "`apps/web` presents it to the API
 * in the `x-carn-share-token` header the router already accepts", so every request here goes
 * to `/api/v1/shared-lists/-/…` and carries the credential out of band. `referrerPolicy:
 * 'no-referrer'` and `cache: 'no-store'` stay on every request — belt and braces now that
 * the URL holds nothing, and still load-bearing for the fragment in the address bar.
 */
export class RemoteApiBackend implements ShoppingListBackend {
  constructor(private readonly options: RemoteApiBackendOptions) {}

  private url(suffix = ''): string {
    const base = this.options.apiBase ?? '';
    return `${base}/api/v1/shared-lists/${TOKEN_IN_HEADER_PATH}${suffix}`;
  }

  private async send(suffix: string, init: RequestInit): Promise<Response> {
    const res = await fetch(this.url(suffix), {
      ...init,
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: {
        accept: 'application/json',
        // R67: the credential travels in a header, never in the URL.
        'x-carn-share-token': this.options.token,
        ...(init.body ? { 'content-type': 'application/json' } : {}),
      },
    });
    if (!res.ok) throw new ShareLinkError(res.status, await readErrorCode(res));
    return res;
  }

  async load(): Promise<LoadedList> {
    const res = await this.send('', { method: 'GET' });
    const body = (await res.json()) as SharedListDto;
    return {
      listId: body.list.id,
      name: body.list.name,
      items: body.items.map((dto) => fromDto(dto, body.list.id)),
    };
  }

  /**
   * The server builds the row with `makeItem` itself, so only the fields it accepts are
   * sent. It deliberately ignores `department`/`capturedPlaceLabel`, so sending them
   * would be noise pretending to be a contract.
   */
  async addItem(item: ListItem): Promise<void> {
    await this.send('/items', {
      method: 'POST',
      body: JSON.stringify({ id: item.id, name: item.name, qualifier: item.qualifier }),
    });
  }

  async setChecked(itemId: string, checked: boolean): Promise<void> {
    await this.send(`/items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ checked }),
    });
  }

  subscribe(onChange: (list: LoadedList) => void): () => void {
    return pollWhileVisible(() => this.load(), onChange);
  }
}

/**
 * brief §7's push-plus-poll, minus the push. Re-reads on an interval and on the tab
 * becoming visible again, and never while it is hidden — see `SHARE_LINK_POLL_MS`.
 */
function pollWhileVisible(
  load: () => Promise<LoadedList>,
  onChange: (list: LoadedList) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    load().then(
      (list) => {
        if (!stopped) onChange(list);
      },
      () => undefined, // The status is reported by whoever owns it, not by the poll.
    );
  };

  const interval = window.setInterval(tick, SHARE_LINK_POLL_MS);
  const onVisible = () => tick();
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

// ---------------------------------------------------------------------------
// Local storage: the preview store AND the offline fallback
// ---------------------------------------------------------------------------

function storageKey(listId: string): string {
  return `carn.web.sharedList.${listId}`;
}

function outboxKey(listId: string): string {
  return `carn.web.sharedListOutbox.${listId}`;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or blocked localStorage must not break the surface. The user's write is
    // still on screen; it is the persistence that failed, and there is nothing here
    // worth an error dialogue over.
  }
}

function readAll(listId: string): ListItem[] {
  return readJson<ListItem[]>(storageKey(listId), []);
}

function writeAll(listId: string, items: readonly ListItem[]): void {
  writeJson(storageKey(listId), items);
}

/**
 * Local-first, using the SAME merge functions the device and the server both use.
 *
 * Two jobs now: (1) the honest preview when there is no token in the URL at all — the
 * local write IS the write, because there is nowhere to send it, and the page says so
 * with `share.localOnly`; (2) the snapshot + offline store `ShareLinkBackend` falls back
 * to when a real link cannot reach the server.
 *
 * Live across browser TABS on the same device via the native `storage` event. That is a
 * small, honest illustration of "add on one side, see it on the other" — not a claim of
 * multi-device sync, which is what a token gets you.
 */
export class LocalStorageBackend implements ShoppingListBackend {
  constructor(private readonly listId: string) {}

  async load(): Promise<LoadedList> {
    return { listId: this.listId, items: readAll(this.listId) };
  }

  async addItem(item: ListItem): Promise<void> {
    writeAll(this.listId, mergeItems(readAll(this.listId), [item]));
  }

  async setChecked(itemId: string, checked: boolean, by: string): Promise<void> {
    const now = Date.now();
    writeAll(
      this.listId,
      readAll(this.listId).map((item) =>
        item.id !== itemId ? item : checked ? checkItem(item, now, by) : uncheckItem(item, now, by),
      ),
    );
  }

  subscribe(onChange: (list: LoadedList) => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(this.listId)) {
        onChange({ listId: this.listId, items: readAll(this.listId) });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }
}

// ---------------------------------------------------------------------------
// The composition R48 asks for
// ---------------------------------------------------------------------------

/**
 * The offline outbox. Deliberately tiny, and deliberately the same two verbs the API
 * grants — there is no third operation to queue because there is no third operation.
 * Ordered; replayed in order; an entry is dropped only once the server has taken it.
 */
type OutboxEntry =
  | { kind: 'add'; item: ListItem }
  | { kind: 'check'; itemId: string; checked: boolean; by: string };

/**
 * Remote-first, local-fallback, and honest about which it is doing.
 *
 * R48's "`RemoteApiBackend` becomes the active backend when a share token is present in
 * the URL, with `LocalStorageBackend` as the offline fallback". The status callback is
 * what lets the page state which one is running rather than implying sync it does not
 * have.
 */
export class ShareLinkBackend implements ShoppingListBackend {
  private readonly remote: RemoteApiBackend;
  private status: SharedListStatus = 'loading';
  /** The real list id, once known. Storage keys use `PENDING_LIST_ID` until then. */
  private listId: string = PENDING_LIST_ID;
  private listName: string | undefined;

  constructor(
    private readonly options: RemoteApiBackendOptions & {
      onStatus?: (status: SharedListStatus) => void;
      onNotice?: (notice: SharedListNotice) => void;
    },
  ) {
    this.remote = new RemoteApiBackend(options);
  }

  private setStatus(next: SharedListStatus): void {
    if (next === this.status) return;
    this.status = next;
    this.options.onStatus?.(next);
  }

  /**
   * A revoked link is a terminal state for this tab, and it is the one state where a write
   * must not even be written locally.
   *
   * R48's promise is an honest preview, not a scratchpad that looks like a list. Writing to
   * `localStorage` here is what produced the failure a critique walked: the visitor typed
   * "bin bags", it went to `localStorage`, it was never queued and never could be, the
   * household never saw it, and the screen said "This phone only for now." The item is not
   * accepted, so it is not stored, and the page renders `sharedList.linkExpired` instead of
   * an add field.
   */
  private get dead(): boolean {
    return this.status === 'revoked';
  }

  private snapshot(): LoadedList {
    return { listId: this.listId, name: this.listName, items: readAll(this.listId) };
  }

  async load(): Promise<LoadedList> {
    try {
      const server = await this.remote.load();
      this.adoptListId(server.listId);
      this.listName = server.name;
      const drained = await this.drainOutbox(server);
      writeAll(this.listId, drained.items);
      this.setStatus('live');
      return drained;
    } catch (err) {
      // 404: the server saying this link opens nothing — unknown, revoked, or the list is
      // gone (`functions/src/lib/share-link.ts` returns one indistinguishable answer for all
      // three on purpose). NOT a network problem, and no longer collapsed into `preview`:
      // `revoked` renders R68's `sharedList.linkExpired` and stops the page pretending a
      // typed item is going anywhere.
      // 429: the server answered and refused. `rateLimited`, with its own sentence.
      // Anything else: genuinely unreachable.
      this.setStatus(statusForError(err));
      return this.snapshot();
    }
  }

  async addItem(item: ListItem): Promise<void> {
    // A dead link takes nothing, not even locally. See `dead`.
    if (this.dead) return;
    // Local first, otherwise: the item is on screen and on disk before the network is asked
    // (implementation-brief §2).
    writeAll(this.listId, mergeItems(readAll(this.listId), [item]));
    try {
      await this.remote.addItem(item);
      this.setStatus('live');
    } catch (err) {
      const next = statusForError(err);
      // A link that died between the load and this write: the item is not queued, because
      // there is nothing to queue it against, and the page is about to say the link is dead
      // rather than claim the item was taken.
      if (next !== 'revoked') this.queue({ kind: 'add', item });
      this.setStatus(next);
    }
  }

  async setChecked(itemId: string, checked: boolean, by: string): Promise<void> {
    if (this.dead) return;
    const now = Date.now();
    writeAll(
      this.listId,
      readAll(this.listId).map((item) =>
        item.id !== itemId ? item : checked ? checkItem(item, now, by) : uncheckItem(item, now, by),
      ),
    );
    try {
      await this.remote.setChecked(itemId, checked);
      this.setStatus('live');
    } catch (err) {
      const next = statusForError(err);
      if (next !== 'revoked') this.queue({ kind: 'check', itemId, checked, by });
      this.setStatus(next);
    }
  }

  subscribe(onChange: (list: LoadedList) => void): () => void {
    return pollWhileVisible(() => this.load(), onChange);
  }

  /** Current status, for a caller that mounts after the first transition. */
  currentStatus(): SharedListStatus {
    return this.status;
  }

  /**
   * Move the local store from `PENDING_LIST_ID` to the real id the first time the server
   * names it, so a write made before the first successful read is not orphaned under a
   * key nothing will look at again.
   */
  private adoptListId(realId: string): void {
    if (this.listId === realId) return;
    if (this.listId === PENDING_LIST_ID) {
      const carried = readAll(PENDING_LIST_ID);
      const queued = readJson<OutboxEntry[]>(outboxKey(PENDING_LIST_ID), []);
      if (carried.length > 0) writeAll(realId, mergeItems(readAll(realId), carried));
      if (queued.length > 0) {
        writeJson(outboxKey(realId), [...readJson<OutboxEntry[]>(outboxKey(realId), []), ...queued]);
      }
      writeAll(PENDING_LIST_ID, []);
      writeJson(outboxKey(PENDING_LIST_ID), []);
    }
    this.listId = realId;
  }

  private queue(entry: OutboxEntry): void {
    writeJson(outboxKey(this.listId), [...readJson<OutboxEntry[]>(outboxKey(this.listId), []), entry]);
  }

  /**
   * Replay whatever was written while the server was unreachable, then return the list
   * as it now stands. Entries that succeed are dropped; entries that fail stay queued and
   * the caller is left `degraded`, so nothing is silently lost and nothing is replayed
   * twice out of order.
   *
   * Replaying an add the server already has is safe: the route answers a repeated id on
   * the same list with a 200 no-op (see `functions/src/lib/share-link.ts`), which is what
   * makes this idempotent without an idempotency key of its own.
   *
   * ONE FAILURE IS NOT RETRIED, AND IS ANNOUNCED. `410 item_removed` means the household has
   * taken that item off the list since the visitor queued it. Retrying forever would be a
   * queue that never drains; dropping it silently is what the old 200-with-the-removed-item
   * answer did, and it is the thing "nothing is silently lost" was claimed against. So the
   * entry is dropped deliberately and `onNotice` carries `sharedList.itemWithdrawn` to the
   * page. Everything else — offline, 429, 500 — stays queued.
   */
  private async drainOutbox(server: LoadedList): Promise<LoadedList> {
    const queued = readJson<OutboxEntry[]>(outboxKey(this.listId), []);
    if (queued.length === 0) return server;

    const unsent: OutboxEntry[] = [];
    let items = server.items;

    for (const entry of queued) {
      try {
        if (entry.kind === 'add') {
          // eslint-disable-next-line no-await-in-loop -- order matters; see the header.
          await this.remote.addItem(entry.item);
          items = mergeItems(items, [entry.item]);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await this.remote.setChecked(entry.itemId, entry.checked);
          const now = Date.now();
          items = items.map((item) =>
            item.id !== entry.itemId
              ? item
              : entry.checked
                ? checkItem(item, now, entry.by)
                : uncheckItem(item, now, entry.by),
          );
        }
      } catch (err) {
        if (err instanceof ShareLinkError && err.code === 'item_removed') {
          // Dropped, not retried, and SAID. `items` deliberately keeps the server's view,
          // which does not contain the row.
          this.options.onNotice?.({
            kind: 'itemWithdrawn',
            itemName: entry.kind === 'add' ? entry.item.name : this.nameFor(entry.itemId, items),
          });
          continue;
        }
        unsent.push(entry);
      }
    }

    writeJson(outboxKey(this.listId), unsent);
    if (unsent.length > 0) this.setStatus('degraded');
    return { ...server, items };
  }

  /**
   * The item's own name for a notice about it, from whatever is still on hand. A check whose
   * row the household has since removed may have no name anywhere any more, and the notice
   * still has to be a sentence — `sharedList.itemWithdrawn` names the thing, so an unnamed
   * fallback is the local snapshot's copy and then, failing that, nothing.
   */
  private nameFor(itemId: string, items: readonly ListItem[]): string {
    const known =
      items.find((item) => item.id === itemId) ??
      readAll(this.listId).find((item) => item.id === itemId);
    return known?.name ?? itemId;
  }
}

/**
 * Choose the backend R48 asks for: the capability-URL client when the URL carries a
 * token, the honest local preview when it does not.
 */
export function makeSharedListBackend(args: {
  token: string | null;
  previewListId: string;
  apiBase?: string;
  onStatus?: (status: SharedListStatus) => void;
  onNotice?: (notice: SharedListNotice) => void;
}): { backend: ShoppingListBackend; initialStatus: SharedListStatus } {
  if (!args.token) {
    return { backend: new LocalStorageBackend(args.previewListId), initialStatus: 'preview' };
  }
  return {
    backend: new ShareLinkBackend({
      token: args.token,
      ...(args.apiBase === undefined ? {} : { apiBase: args.apiBase }),
      ...(args.onStatus === undefined ? {} : { onStatus: args.onStatus }),
      ...(args.onNotice === undefined ? {} : { onNotice: args.onNotice }),
    }),
    initialStatus: 'loading',
  };
}

export { isChecked, makeItem };
